import React, { useEffect, useState } from 'react';
import ExportBar from '../components/ExportBar';
import { api } from '../App';

// Normalize human-readable values to Title Case so the UI renders consistently
// regardless of how the ERP stored them (UPPERCASE / Proper Case / mixed).
// Leaves part-codes (alphanumeric with digits, no spaces, len>6) alone.
function titleCase(s) {
  if (!s) return s;
  const str = String(s);
  if (!/\s/.test(str) && /[0-9]/.test(str) && str.length > 6) return str;
  return str.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function priColor(p) {
  return p === 'High' ? 'var(--red)' : p === 'Low' ? 'var(--text3)' : 'var(--yellow)';
}
function statusColor(s) {
  return s === 'Assigned' ? 'var(--yellow)'
       : s === 'Hold'      ? 'var(--teal)'
       : s === 'Completed' ? 'var(--green)'
       :                     'var(--text3)';
}

export default function Tasks({ user, onBack }) {
  const [view, setView] = useState('list');           // 'list' | 'detail' | 'new'
  const [filter, setFilter] = useState('assigned');   // 'assigned' | 'created' | 'all'
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [users, setUsers] = useState([]);

  const accent = 'var(--gold)';

  function loadTasks() {
    setLoading(true);
    api(`/tasks?filter=${filter}`).then(d => {
      setTasks(Array.isArray(d) ? d : []);
      setLoading(false);
    });
  }
  function loadUsers() {
    api('/tasks/users').then(d => setUsers(Array.isArray(d) ? d : []));
  }

  useEffect(() => { loadTasks(); }, [filter]);
  useEffect(() => { loadUsers(); }, []);

  // ────── DETAIL VIEW ──────
  if (view === 'detail' && selectedId) {
    return <TaskDetail
      taskId={selectedId}
      user={user}
      onBack={() => { setView('list'); setSelectedId(null); loadTasks(); }}
    />;
  }

  // ────── NEW TASK VIEW ──────
  if (view === 'new') {
    return <NewTask
      users={users}
      onBack={() => setView('list')}
      onCreated={(id) => { loadTasks(); setSelectedId(id); setView('detail'); }}
    />;
  }

  // ────── LIST VIEW ──────
  const grouped = tasks.reduce((m, t) => {
    if (!m[t.status]) m[t.status] = [];
    m[t.status].push(t);
    return m;
  }, {});
  const order = ['Assigned', 'Hold', 'Completed', 'Closed'];

  return (
    <div className="screen">
      <div className="header">
        <div>
          <div className="header-logo">RISHAB</div>
          <div className="header-sub">Task Management</div>
        </div>
        <button onClick={() => setView('new')} style={{
          background: accent, color: 'var(--bg)', border: 'none', borderRadius: 6,
          padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 1
        }}>+ NEW TASK</button>
      </div>

      <ExportBar compact reportKey={`tasks-${filter}`} title={`Tasks — ${filter}`} sections={[{ name: 'Tasks', columns: [{ key: 'id', label: '#', type: 'int' }, { key: 'title', label: 'Task' }, { key: 'status', label: 'Status' }, { key: 'priority', label: 'Priority' }, { key: 'due_date', label: 'Due', type: 'date' }, { key: 'assignees_txt', label: 'Assignees' }, { key: 'linked_tas', label: 'TAS' }], rows: (tasks || []).map(t => ({ ...t, assignees_txt: Array.isArray(t.assignees) ? t.assignees.map(a => a.name || a.display_name || a).join(', ') : (t.assignees || '') })) }]} source={{ endpoint: '/tasks', params: { filter } }} />

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {[
          { key: 'assigned', label: 'ASSIGNED TO ME' },
          { key: 'created',  label: 'CREATED BY ME' },
          { key: 'all',      label: 'ALL' },
        ].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{
              padding: '7px 14px', borderRadius: 18, fontSize: 11, letterSpacing: 1.2, fontWeight: 600,
              background: filter === t.key ? `${accent}26` : 'transparent',
              border: `1px solid ${filter === t.key ? accent : 'var(--border)'}`,
              color: filter === t.key ? accent : 'var(--text3)',
              cursor: 'pointer'
            }}>{t.label}</button>
        ))}
      </div>

      {/* List grouped by status */}
      {loading && <div className="loading">LOADING TASKS…</div>}
      {!loading && tasks.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
          No tasks {filter === 'assigned' ? 'assigned to you' : filter === 'created' ? 'created by you' : 'yet'}.
          <br /><br />
          <button onClick={() => setView('new')} style={{
            background: accent, color: 'var(--bg)', border: 'none', borderRadius: 6,
            padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700, letterSpacing: 1, marginTop: 10
          }}>+ CREATE FIRST TASK</button>
        </div>
      )}
      {!loading && order.map(st => grouped[st] && (
        <div key={st}>
          <div style={{ padding: '10px 16px 4px', fontSize: 10, color: statusColor(st), letterSpacing: 2, fontWeight: 600 }}>
            {st.toUpperCase()} ({grouped[st].length})
          </div>
          {grouped[st].map(t => (
            <div key={t.id}
              onClick={() => { setSelectedId(t.id); setView('detail'); }}
              style={{
                margin: '0 16px 8px', padding: '14px 16px',
                background: t.unread ? 'var(--gold-bg)' : 'var(--bg2)',
                border: 'none',
                borderLeft: `3px solid ${statusColor(st)}`,
                borderRadius: 12, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 6,
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = t.unread ? 'var(--gold-bg)' : 'var(--bg2)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: `${priColor(t.priority)}22`, color: priColor(t.priority), letterSpacing: 1, fontWeight: 600 }}>{t.priority}</span>
                    {t.unread > 0 && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: 'rgba(232,114,124,0.2)', color: 'var(--red)', fontWeight: 600 }}>NEW</span>}
                    {t.linked_tas && <span style={{ fontSize: 10, color: 'var(--teal)' }}>🧵 {t.linked_tas}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginTop: 4 }}>#{t.id} · {t.title}</div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmtDate(t.updated_at)}</div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                <span>👤 {t.created_by_name || '—'} → {t.assignees || '—'}</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  {t.comment_count > 0 && <span>💬 {t.comment_count}</span>}
                  {t.due_date && <span>📅 due {fmtDate(t.due_date)}</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TASK DETAIL VIEW
// ─────────────────────────────────────────────────────────────
function TaskDetail({ taskId, user, onBack }) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [users, setUsers] = useState([]);
  const [forwardIds, setForwardIds] = useState([]);
  const [forwarding, setForwarding] = useState(false);

  function load() {
    setLoading(true);
    api(`/tasks/${taskId}`).then(d => { setTask(d); setLoading(false); });
  }
  useEffect(() => { load(); }, [taskId]);
  useEffect(() => { api('/tasks/users').then(d => setUsers(Array.isArray(d) ? d : [])); }, []);

  async function forward() {
    if (forwardIds.length === 0) return;
    setForwarding(true);
    const assignees = users.filter(u => forwardIds.includes(u.id))
      .map(u => ({ id: u.id, name: u.display_name }));
    await api(`/tasks/${taskId}/forward`, { method: 'POST', body: JSON.stringify({ assignees }) });
    setForwardIds([]);
    setShowForward(false);
    setForwarding(false);
    load();
  }

  async function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    await api(`/tasks/${taskId}/comment`, { method: 'POST', body: JSON.stringify({ comment }) });
    setComment('');
    setPosting(false);
    load();
  }

  async function changeStatus(newStatus) {
    if (!confirm(`Change status to ${newStatus}?`)) return;
    await api(`/tasks/${taskId}/status`, { method: 'POST', body: JSON.stringify({ status: newStatus }) });
    load();
  }

  if (loading) return <div className="screen"><div className="loading">LOADING TASK…</div></div>;
  if (!task) return <div className="screen"><div className="loading">Task not found</div></div>;

  return (
    <div className="screen">
      <div className="header">
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ flex: 1, marginLeft: 12 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 18, color: 'var(--gold)' }}>Task #{task.id}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>by {titleCase(task.created_by_name)} · {fmtDate(task.created_at)}</div>
        </div>
        <span style={{ fontSize: 10, padding: '4px 10px', borderRadius: 12, background: `${statusColor(task.status)}22`, color: statusColor(task.status), letterSpacing: 1, fontWeight: 700 }}>{task.status}</span>
      </div>

      {/* Title + meta */}
      <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600, lineHeight: 1.3 }}>{task.title}</div>
        {task.description && (
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{task.description}</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12, fontSize: 11, color: 'var(--text3)' }}>
          <span style={{ color: priColor(task.priority) }}>● {task.priority} priority</span>
          {task.due_date && <span>📅 Due {fmtDate(task.due_date)}</span>}
          {task.linked_tas && <span style={{ color: 'var(--teal)' }}>🧵 Linked: {task.linked_tas}</span>}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
          <strong style={{ color: 'var(--text2)' }}>Assignees:</strong>{' '}
          {(task.assignees || []).map((a, i) => (
            <span key={a.user_id} style={{ color: 'var(--text)' }}>
              {a.user_name}{a.read_at ? <span style={{ color: 'var(--green)' }} title={`read ${fmtDateTime(a.read_at)}`}> ✓</span> : <span style={{ color: 'var(--red)' }}> •</span>}
              {i < task.assignees.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </div>

      {/* Status + Forward actions */}
      <div style={{ display: 'flex', gap: 6, padding: 14, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {['Assigned', 'Hold', 'Completed', 'Closed'].filter(s => s !== task.status).map(s => (
          <button key={s} onClick={() => changeStatus(s)}
            style={{
              padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600,
              background: 'transparent', border: `1px solid ${statusColor(s)}`,
              color: statusColor(s), cursor: 'pointer', letterSpacing: 0.5
            }}>Mark {s}</button>
        ))}
        <button onClick={() => setShowForward(true)}
          style={{
            padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600,
            background: 'rgba(91,194,199,0.10)', border: '1px solid #5BC2C7',
            color: 'var(--teal)', cursor: 'pointer', letterSpacing: 0.5
          }}>↗ Forward</button>
      </div>

      {/* Forward modal */}
      {showForward && (() => {
        const currentIds = new Set((task.assignees || []).map(a => a.user_id));
        const availableUsers = users.filter(u => !currentIds.has(u.id));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
               onClick={() => !forwarding && setShowForward(false)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 12, padding: 18, maxWidth: 460, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 16, color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>Forward Task #{task.id}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
                Add more people to this task. They'll see it in their Tasks tab and get notified.
              </div>
              {availableUsers.length === 0 ? (
                <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>All users are already assigned.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {availableUsers.map(u => (
                    <button key={u.id} onClick={() => setForwardIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id])}
                      style={{
                        padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: forwardIds.includes(u.id) ? 'rgba(212,185,120,0.2)' : 'transparent',
                        border: `1px solid ${forwardIds.includes(u.id) ? 'var(--gold)' : '#332E66'}`,
                        color: forwardIds.includes(u.id) ? 'var(--gold)' : 'var(--text3)'
                      }}>{u.display_name}</button>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowForward(false)} disabled={forwarding}
                  style={{ background: 'transparent', border: '1px solid #332E66', color: 'var(--text3)', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                <button onClick={forward} disabled={forwarding || forwardIds.length === 0}
                  style={{ background: 'var(--gold)', color: 'var(--bg)', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: forwarding ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, opacity: forwarding || forwardIds.length === 0 ? 0.5 : 1 }}>
                  {forwarding ? 'FORWARDING…' : `↗ FORWARD (${forwardIds.length})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Comments */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 2, fontWeight: 600, marginBottom: 10 }}>
          COMMENTS ({task.comments.length})
        </div>
        {task.comments.length === 0 && (
          <div style={{ color: 'var(--text3)', fontSize: 11, textAlign: 'center', padding: 12 }}>No comments yet</div>
        )}
        {task.comments.map(c => (
          <div key={c.id} style={{
            margin: '0 0 10px 0', padding: '10px 12px',
            background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)',
            borderLeft: `3px solid ${statusColor(c.status_at_time || 'Assigned')}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
              <strong style={{ color: 'var(--gold)' }}>{c.user_name || 'Unknown'}</strong>
              <span style={{ color: 'var(--text3)' }}>{fmtDateTime(c.created_at)}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{c.comment}</div>
            {c.status_at_time && <div style={{ fontSize: 9, color: statusColor(c.status_at_time), marginTop: 4, letterSpacing: 1 }}>STATUS: {c.status_at_time.toUpperCase()}</div>}
          </div>
        ))}
      </div>

      {/* New comment box */}
      <div style={{ padding: 14 }}>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Reply…"
          rows={3}
          style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 12px', fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <button onClick={postComment} disabled={posting || !comment.trim()}
          style={{
            marginTop: 8, background: 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 6,
            padding: '10px 20px', cursor: posting ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 1,
            opacity: posting || !comment.trim() ? 0.5 : 1
          }}>{posting ? 'POSTING…' : 'POST COMMENT'}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NEW TASK FORM
// ─────────────────────────────────────────────────────────────
function NewTask({ users, onBack, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [dueDate, setDueDate] = useState('');
  const [linkedTas, setLinkedTas] = useState('');
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function toggleAssignee(uid) {
    setAssigneeIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  }

  async function save() {
    if (!title.trim()) return setErr('Title is required');
    if (assigneeIds.length === 0) return setErr('Select at least one assignee');
    setSaving(true);
    setErr('');
    const assignees = users.filter(u => assigneeIds.includes(u.id))
      .map(u => ({ id: u.id, name: u.display_name }));
    try {
      const res = await api('/tasks', {
        method: 'POST',
        body: JSON.stringify({ title, description, priority, due_date: dueDate || null, linked_tas: linkedTas || null, assignees })
      });
      if (res.success) onCreated(res.id);
      else setErr(res.error || 'Failed to create');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="screen">
      <div className="header">
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ flex: 1, marginLeft: 12 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: 'var(--gold)' }}>New Task</div>
        </div>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>TITLE *</div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Short summary…"
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '10px 12px', fontSize: 13 }} />
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>DESCRIPTION</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Describe in detail…"
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 130px' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>PRIORITY</div>
            <select value={priority} onChange={e => setPriority(e.target.value)}
              style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '10px 12px', fontSize: 13 }}>
              <option>High</option><option>Medium</option><option>Low</option>
            </select>
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>DUE DATE</div>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '10px 12px', fontSize: 13 }} />
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>LINK TAS (opt)</div>
            <input value={linkedTas} onChange={e => setLinkedTas(e.target.value)} placeholder="e.g. TAS/625"
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '10px 12px', fontSize: 13 }} />
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginBottom: 5 }}>ASSIGN TO * ({assigneeIds.length} selected)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {users.map(u => (
              <button key={u.id} onClick={() => toggleAssignee(u.id)}
                style={{
                  padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: assigneeIds.includes(u.id) ? 'rgba(212,185,120,0.2)' : 'transparent',
                  border: `1px solid ${assigneeIds.includes(u.id) ? 'var(--gold)' : 'var(--border)'}`,
                  color: assigneeIds.includes(u.id) ? 'var(--gold)' : 'var(--text3)'
                }}>{u.display_name}</button>
            ))}
          </div>
        </div>

        {err && <div style={{ color: 'var(--red)', fontSize: 12 }}>{err}</div>}
        <button onClick={save} disabled={saving}
          style={{
            background: 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 6,
            padding: '12px 20px', cursor: saving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700, letterSpacing: 1,
            opacity: saving ? 0.5 : 1
          }}>{saving ? 'CREATING…' : '✓ CREATE TASK'}</button>
      </div>
    </div>
  );
}
